import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDateISO, formatDisplayDate, parseDateISO, todayISO } from '../../utils/dateHelpers';
import { useClickOutside } from '../../hooks/useClickOutside';
import { CalendarMonth } from './CalendarMonth';
import type { DayData } from './CalendarMonth/CalendarMonth.types';
import {
  Dropdown,
  LoadingOverlay,
  MonthsContainer,
  NavButton,
  NavRow,
  TriggerButton,
  TriggerLabel,
  TriggerRow,
  TriggerValue,
  Wrapper,
} from './DateRangePicker.styles';
import type { DateRangePickerProps, SelectionPhase } from './DateRangePicker.types';

function getMaxCheckoutDate(
  checkInDate: string,
  availability?: Map<string, DayData>
): string | null {
  if (!availability) return null;

  const start = parseDateISO(checkInDate);
  const maxLookahead = 365;

  for (let i = 1; i <= maxLookahead; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const ds = formatDateISO(d);
    const dayData = availability.get(ds);

    if (dayData && (dayData.type === 'unavailable' || dayData.type === 'hotel_closed')) {
      const prev = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i - 1);
      return formatDateISO(prev);
    }
  }

  return null;
}

export function DateRangePicker({
  checkIn: externalCheckIn,
  checkOut: externalCheckOut,
  singleDateMode = false,
  availability,
  isLoading,
  onRangeSelect,
  onMonthChange,
  renderInline = false,
  columnView = false,
  labels: l,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(renderInline);
  const [phase, setPhase] = useState<SelectionPhase>('check-in');
  const [internalCheckIn, setInternalCheckIn] = useState<string | null>(externalCheckIn ?? null);
  const [internalCheckOut, setInternalCheckOut] = useState<string | null>(externalCheckOut ?? null);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  const [visibleMonth, setVisibleMonth] = useState<Date>(() => {
    if (externalCheckIn) {
      const d = parseDateISO(externalCheckIn);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInternalCheckIn(externalCheckIn ?? null);
    setInternalCheckOut(externalCheckOut ?? null);
  }, [externalCheckIn, externalCheckOut]);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
    if (phase === 'check-out') {
      setPhase('check-in');
      setInternalCheckIn(externalCheckIn ?? null);
      setInternalCheckOut(externalCheckOut ?? null);
    }
  }, [phase, externalCheckIn, externalCheckOut]);

  useClickOutside(wrapperRef, handleClickOutside, !renderInline);

  const maxCheckout =
    phase === 'check-out' && internalCheckIn
      ? getMaxCheckoutDate(internalCheckIn, availability)
      : null;

  const effAvailability = useMemo((): Map<string, DayData> | undefined => {
    if (!availability) return undefined;
    if (phase !== 'check-out' || !internalCheckIn || !maxCheckout) return availability;

    const map = new Map(availability);
    for (const [dateStr, data] of map) {
      if (dateStr > maxCheckout && data.type === 'available') {
        map.set(dateStr, { ...data, type: 'unavailable' });
      }
    }
    return map;
  }, [availability, phase, internalCheckIn, maxCheckout]);

  const goToPrevMonth = () => {
    const prev = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1);
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    if (prev >= currentMonth) {
      setVisibleMonth(prev);
      onMonthChange?.(prev);
    }
  };

  const goToNextMonth = () => {
    const next = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);
    setVisibleMonth(next);
    onMonthChange?.(next);
  };

  const now = new Date();
  const canGoPrev =
    visibleMonth.getFullYear() > now.getFullYear() ||
    (visibleMonth.getFullYear() === now.getFullYear() && visibleMonth.getMonth() > now.getMonth());

  const handleDayClick = (dateStr: string) => {
    const today = todayISO();
    if (dateStr < today) return;

    if (singleDateMode) {
      setInternalCheckIn(dateStr);
      setInternalCheckOut(null);
      onRangeSelect(dateStr, dateStr);
      return;
    }

    if (phase === 'check-in') {
      setInternalCheckIn(dateStr);
      setInternalCheckOut(null);
      setPhase('check-out');
    } else {
      if (dateStr <= internalCheckIn!) {
        setInternalCheckIn(dateStr);
        setInternalCheckOut(null);
        setPhase('check-out');
      } else {
        setInternalCheckOut(dateStr);
        setPhase('check-in');
        onRangeSelect(internalCheckIn!, dateStr);
      }
    }
  };

  const handleTriggerClick = (which: 'check-in' | 'check-out') => {
    if (isOpen && phase === which) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      setPhase(which === 'check-out' && internalCheckIn ? 'check-out' : 'check-in');
    }
  };

  const secondMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);

  const calendarLabels =
    l?.weekdays || l?.months ? { weekdays: l.weekdays, months: l.months } : undefined;

  const calendarPanel = (
    <>
      <NavRow>
        <NavButton
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          type="button"
          aria-label={l?.previousMonth ?? 'Previous month'}
        >
          &#8249;
        </NavButton>
        <NavButton onClick={goToNextMonth} type="button" aria-label={l?.nextMonth ?? 'Next month'}>
          &#8250;
        </NavButton>
      </NavRow>

      <MonthsContainer $columnView={columnView}>
        <CalendarMonth
          year={visibleMonth.getFullYear()}
          month={visibleMonth.getMonth()}
          availability={effAvailability}
          checkIn={internalCheckIn}
          checkOut={internalCheckOut}
          isSelectingCheckOut={phase === 'check-out'}
          hoveredDate={hoveredDate}
          onDayClick={handleDayClick}
          onDayHover={setHoveredDate}
          labels={calendarLabels}
        />
        <CalendarMonth
          year={secondMonth.getFullYear()}
          month={secondMonth.getMonth()}
          availability={effAvailability}
          checkIn={internalCheckIn}
          checkOut={internalCheckOut}
          isSelectingCheckOut={phase === 'check-out'}
          hoveredDate={hoveredDate}
          onDayClick={handleDayClick}
          onDayHover={setHoveredDate}
          labels={calendarLabels}
        />
      </MonthsContainer>

      {isLoading && (
        <LoadingOverlay>{l?.loadingAvailability ?? 'Loading availability...'}</LoadingOverlay>
      )}
    </>
  );

  if (renderInline) {
    return (
      <Dropdown $columnView={columnView} $inline>
        {calendarPanel}
      </Dropdown>
    );
  }

  return (
    <Wrapper ref={wrapperRef}>
      <TriggerRow>
        {singleDateMode ? (
          <TriggerButton
            $hasValue={!!internalCheckIn}
            onClick={() => handleTriggerClick('check-in')}
            type="button"
          >
            <TriggerLabel>{l?.date ?? 'Date'}</TriggerLabel>
            <TriggerValue $placeholder={!internalCheckIn}>
              {(internalCheckIn && formatDisplayDate(internalCheckIn)) ||
                (l?.selectDate ?? 'Select date')}
            </TriggerValue>
          </TriggerButton>
        ) : (
          <>
            <TriggerButton
              $hasValue={!!internalCheckIn}
              onClick={() => handleTriggerClick('check-in')}
              type="button"
            >
              <TriggerLabel>{l?.checkIn ?? 'Check in'}</TriggerLabel>
              <TriggerValue $placeholder={!internalCheckIn}>
                {(internalCheckIn && formatDisplayDate(internalCheckIn)) ||
                  (l?.selectDate ?? 'Select date')}
              </TriggerValue>
            </TriggerButton>
            <TriggerButton
              $hasValue={!!internalCheckOut}
              onClick={() => handleTriggerClick('check-out')}
              type="button"
            >
              <TriggerLabel>{l?.checkOut ?? 'Check out'}</TriggerLabel>
              <TriggerValue $placeholder={!internalCheckOut}>
                {(internalCheckOut && formatDisplayDate(internalCheckOut)) ||
                  (l?.selectDate ?? 'Select date')}
              </TriggerValue>
            </TriggerButton>
          </>
        )}
      </TriggerRow>

      {isOpen && <Dropdown $columnView={columnView}>{calendarPanel}</Dropdown>}
    </Wrapper>
  );
}
